import { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Shield, AlertTriangle, Activity, Heart, Users, Baby,
  Clock, TrendingUp, Brain, Leaf, Save, Search, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

// ─── Section definitions ──────────────────────────────────────────────────────

const SECCIONES = [
  { id: 'vacunacion',       label: 'Vacunación',         icon: Shield },
  { id: 'alergias',         label: 'Alergias',            icon: AlertTriangle },
  { id: 'patologicos',      label: 'Patológicos',         icon: Activity },
  { id: 'noPatologicos',    label: 'No Patológicos',      icon: Heart },
  { id: 'heredofamiliares', label: 'Heredofamiliares',    icon: Users },
  { id: 'gineco',           label: 'Gineco-Obstétrico',   icon: Baby },
  { id: 'perinatales',      label: 'Perinatales',         icon: Clock },
  { id: 'postnatales',      label: 'Postnatales',         icon: TrendingUp },
  { id: 'psiquiatricos',    label: 'Psiquiátricos',       icon: Brain },
  { id: 'dieta',            label: 'Dieta Nutricional',   icon: Leaf },
] as const;

type SeccionId = typeof SECCIONES[number]['id'];

// Secciones que dependen del perfil de la clínica (configuración > tipo de clínica).
// Las demás (vacunación, alergias, patológicos, no patológicos, heredofamiliares) aplican a cualquier perfil.
const SECCIONES_CONFIGURABLES = new Set<SeccionId>([
  'gineco', 'perinatales', 'postnatales', 'psiquiatricos', 'dieta',
]);

const SECCION_KEYS: Record<SeccionId, string> = {
  vacunacion:       'vacunacion',
  alergias:         'alergiasEspecificas',
  patologicos:      'patologicos',
  noPatologicos:    'noPatologicos',
  heredofamiliares: 'heredofamiliares',
  gineco:           'ginecoObstetricos',
  perinatales:      'perinatales',
  postnatales:      'postnatales',
  psiquiatricos:    'psiquiatricos',
  dieta:            'dietaNutricional',
};

// ─── Catalog data ─────────────────────────────────────────────────────────────

const VACUNAS_ESQUEMA = [
  { id: 'bcg',             nombre: 'BCG',                    desc: 'Tuberculosis — Al nacer' },
  { id: 'hepb_1',          nombre: 'Hepatitis B (1ª dosis)', desc: 'Al nacer' },
  { id: 'pentavalente_1',  nombre: 'Pentavalente (1ª)',       desc: 'DPT + Hib + HB — 2 meses' },
  { id: 'neumococo_1',     nombre: 'Neumococo (1ª)',          desc: '2 meses' },
  { id: 'rotavirus_1',     nombre: 'Rotavirus (1ª)',          desc: '2 meses' },
  { id: 'pentavalente_2',  nombre: 'Pentavalente (2ª)',       desc: '4 meses' },
  { id: 'neumococo_2',     nombre: 'Neumococo (2ª)',          desc: '4 meses' },
  { id: 'rotavirus_2',     nombre: 'Rotavirus (2ª)',          desc: '4 meses' },
  { id: 'pentavalente_3',  nombre: 'Pentavalente (3ª)',       desc: '6 meses' },
  { id: 'influenza_1',     nombre: 'Influenza (1ª)',          desc: '6 meses' },
  { id: 'srp',             nombre: 'SRP – Triple viral',      desc: 'Sarampión, Rubéola, Parotiditis — 12 meses' },
  { id: 'neumococo_ref',   nombre: 'Neumococo (refuerzo)',    desc: '12 meses' },
  { id: 'varicela_1',      nombre: 'Varicela (1ª)',           desc: '12 meses' },
  { id: 'dpt_ref',         nombre: 'DPT (4ª dosis)',          desc: '2 años' },
  { id: 'varicela_ref',    nombre: 'Varicela (refuerzo)',     desc: '2 años' },
  { id: 'sr',              nombre: 'SR – Doble viral',        desc: '6 años' },
  { id: 'vph',             nombre: 'VPH',                    desc: 'Virus del Papiloma Humano — 11-12 años' },
  { id: 'tdpa',            nombre: 'Tdpa (adultos)',          desc: 'Refuerzo adultos' },
  { id: 'influenza_anual', nombre: 'Influenza (anual)',       desc: 'Refuerzo anual' },
  { id: 'covid19',         nombre: 'COVID-19',               desc: 'Serie primaria + refuerzos' },
  { id: 'hepatitis_a',     nombre: 'Hepatitis A',            desc: 'Variable' },
  { id: 'meningococo',     nombre: 'Meningococo',            desc: 'Según indicación médica' },
  { id: 'neumococo_23v',   nombre: 'Neumococo 23v (adulto)', desc: 'Mayores de 60 años' },
  { id: 'zoster',          nombre: 'Herpes zoster',          desc: 'Mayores de 50 años' },
  { id: 'fiebre_amarilla', nombre: 'Fiebre amarilla',        desc: 'Viaje / exposición' },
];

const ALERGIAS_CATALOGO: { id: number; nombre: string; categoria: string }[] = [
  { id: 1,   nombre: 'Penicilina', categoria: 'Medicamento' },
  { id: 2,   nombre: 'Amoxicilina', categoria: 'Medicamento' },
  { id: 3,   nombre: 'Ampicilina', categoria: 'Medicamento' },
  { id: 4,   nombre: 'Cefalosporinas', categoria: 'Medicamento' },
  { id: 5,   nombre: 'Sulfamidas', categoria: 'Medicamento' },
  { id: 6,   nombre: 'Trimetoprima-sulfametoxazol', categoria: 'Medicamento' },
  { id: 7,   nombre: 'Macrólidos (eritromicina, claritromicina)', categoria: 'Medicamento' },
  { id: 8,   nombre: 'Tetraciclinas', categoria: 'Medicamento' },
  { id: 9,   nombre: 'Vancomicina', categoria: 'Medicamento' },
  { id: 10,  nombre: 'Fluoroquinolonas (ciprofloxacino, levofloxacino)', categoria: 'Medicamento' },
  { id: 11,  nombre: 'Aspirina (ácido acetilsalicílico)', categoria: 'Medicamento' },
  { id: 12,  nombre: 'Ibuprofeno', categoria: 'Medicamento' },
  { id: 13,  nombre: 'Naproxeno', categoria: 'Medicamento' },
  { id: 14,  nombre: 'Diclofenaco', categoria: 'Medicamento' },
  { id: 15,  nombre: 'AINEs (general)', categoria: 'Medicamento' },
  { id: 16,  nombre: 'Paracetamol / Acetaminofén', categoria: 'Medicamento' },
  { id: 17,  nombre: 'Codeína', categoria: 'Medicamento' },
  { id: 18,  nombre: 'Morfina', categoria: 'Medicamento' },
  { id: 19,  nombre: 'Tramadol', categoria: 'Medicamento' },
  { id: 20,  nombre: 'Anestésicos locales (lidocaína, procaína)', categoria: 'Medicamento' },
  { id: 21,  nombre: 'Anestésicos generales', categoria: 'Medicamento' },
  { id: 22,  nombre: 'Relajantes musculares', categoria: 'Medicamento' },
  { id: 23,  nombre: 'Insulina', categoria: 'Medicamento' },
  { id: 24,  nombre: 'Heparina', categoria: 'Medicamento' },
  { id: 25,  nombre: 'Warfarina', categoria: 'Medicamento' },
  { id: 26,  nombre: 'Contraste yodado (radiológico)', categoria: 'Medicamento' },
  { id: 27,  nombre: 'Contraste con gadolinio', categoria: 'Medicamento' },
  { id: 28,  nombre: 'Vacunas (componentes: gelatina, huevo, neomicina)', categoria: 'Medicamento' },
  { id: 29,  nombre: 'Anticonvulsivantes (carbamazepina, fenitoína)', categoria: 'Medicamento' },
  { id: 30,  nombre: 'Alopurinol', categoria: 'Medicamento' },
  { id: 31,  nombre: 'Quimioterapéuticos (platinos, taxanos)', categoria: 'Medicamento' },
  { id: 32,  nombre: 'Antirretrovirales', categoria: 'Medicamento' },
  { id: 33,  nombre: 'Inhibidores de la bomba de protones', categoria: 'Medicamento' },
  { id: 34,  nombre: 'Estatinas', categoria: 'Medicamento' },
  { id: 35,  nombre: 'Corticosteroides', categoria: 'Medicamento' },
  { id: 36,  nombre: 'Óxido de etileno (esterilización)', categoria: 'Medicamento' },
  { id: 37,  nombre: 'Leche de vaca', categoria: 'Alimento' },
  { id: 38,  nombre: 'Huevo', categoria: 'Alimento' },
  { id: 39,  nombre: 'Maní / cacahuate', categoria: 'Alimento' },
  { id: 40,  nombre: 'Nuez de árbol (nuez, almendra, avellana)', categoria: 'Alimento' },
  { id: 41,  nombre: 'Pistache', categoria: 'Alimento' },
  { id: 42,  nombre: 'Anacardo (nuez de la India)', categoria: 'Alimento' },
  { id: 43,  nombre: 'Trigo / gluten', categoria: 'Alimento' },
  { id: 44,  nombre: 'Soya', categoria: 'Alimento' },
  { id: 45,  nombre: 'Pescado', categoria: 'Alimento' },
  { id: 46,  nombre: 'Crustáceos (camarón, cangrejo, langosta)', categoria: 'Alimento' },
  { id: 47,  nombre: 'Moluscos (almeja, mejillón, pulpo)', categoria: 'Alimento' },
  { id: 48,  nombre: 'Sésamo (ajonjolí)', categoria: 'Alimento' },
  { id: 49,  nombre: 'Mostaza', categoria: 'Alimento' },
  { id: 50,  nombre: 'Apio', categoria: 'Alimento' },
  { id: 51,  nombre: 'Altramuz (lupino)', categoria: 'Alimento' },
  { id: 52,  nombre: 'Chocolate / cacao', categoria: 'Alimento' },
  { id: 53,  nombre: 'Fresa', categoria: 'Alimento' },
  { id: 54,  nombre: 'Kiwi', categoria: 'Alimento' },
  { id: 55,  nombre: 'Plátano', categoria: 'Alimento' },
  { id: 56,  nombre: 'Aguacate', categoria: 'Alimento' },
  { id: 57,  nombre: 'Tomate', categoria: 'Alimento' },
  { id: 58,  nombre: 'Sulfitos (conservador)', categoria: 'Alimento' },
  { id: 59,  nombre: 'Glutamato monosódico', categoria: 'Alimento' },
  { id: 60,  nombre: 'Colorantes artificiales (tartrazina)', categoria: 'Alimento' },
  { id: 61,  nombre: 'Carne de res', categoria: 'Alimento' },
  { id: 62,  nombre: 'Carne de cerdo', categoria: 'Alimento' },
  { id: 63,  nombre: 'Gluten (celiaquía / sensibilidad)', categoria: 'Alimento' },
  { id: 64,  nombre: 'Maíz', categoria: 'Alimento' },
  { id: 65,  nombre: 'Polen de árboles (abedul, roble, olivo)', categoria: 'Ambiental' },
  { id: 66,  nombre: 'Polen de pastos (gramíneas)', categoria: 'Ambiental' },
  { id: 67,  nombre: 'Polen de malezas (ambrosía)', categoria: 'Ambiental' },
  { id: 68,  nombre: 'Ácaros del polvo', categoria: 'Ambiental' },
  { id: 69,  nombre: 'Moho / hongos ambientales', categoria: 'Ambiental' },
  { id: 70,  nombre: 'Caspa de gato', categoria: 'Ambiental' },
  { id: 71,  nombre: 'Caspa de perro', categoria: 'Ambiental' },
  { id: 72,  nombre: 'Caspa de caballo', categoria: 'Ambiental' },
  { id: 73,  nombre: 'Plumas de aves', categoria: 'Ambiental' },
  { id: 74,  nombre: 'Cucarachas', categoria: 'Ambiental' },
  { id: 75,  nombre: 'Roedores (orina/pelo)', categoria: 'Ambiental' },
  { id: 76,  nombre: 'Humo de tabaco', categoria: 'Ambiental' },
  { id: 77,  nombre: 'Contaminación / smog', categoria: 'Ambiental' },
  { id: 78,  nombre: 'Frío (urticaria por frío)', categoria: 'Ambiental' },
  { id: 79,  nombre: 'Calor / sudoración (urticaria colinérgica)', categoria: 'Ambiental' },
  { id: 80,  nombre: 'Luz solar (fotosensibilidad)', categoria: 'Ambiental' },
  { id: 81,  nombre: 'Picadura de abeja', categoria: 'Insecto/Veneno' },
  { id: 82,  nombre: 'Picadura de avispa', categoria: 'Insecto/Veneno' },
  { id: 83,  nombre: 'Picadura de avispón', categoria: 'Insecto/Veneno' },
  { id: 84,  nombre: 'Picadura de hormiga de fuego', categoria: 'Insecto/Veneno' },
  { id: 85,  nombre: 'Picadura de mosquito', categoria: 'Insecto/Veneno' },
  { id: 86,  nombre: 'Picadura de garrapata (alfa-gal)', categoria: 'Insecto/Veneno' },
  { id: 87,  nombre: 'Mordedura/veneno de araña', categoria: 'Insecto/Veneno' },
  { id: 88,  nombre: 'Veneno de serpiente (antiveneno)', categoria: 'Insecto/Veneno' },
  { id: 89,  nombre: 'Látex', categoria: 'Contacto' },
  { id: 90,  nombre: 'Níquel', categoria: 'Contacto' },
  { id: 91,  nombre: 'Cromo', categoria: 'Contacto' },
  { id: 92,  nombre: 'Cobalto', categoria: 'Contacto' },
  { id: 93,  nombre: 'Fragancias / perfumes', categoria: 'Contacto' },
  { id: 94,  nombre: 'Cosméticos (conservadores, parabenos)', categoria: 'Contacto' },
  { id: 95,  nombre: 'Tintes para cabello (parafenilendiamina)', categoria: 'Contacto' },
  { id: 96,  nombre: 'Detergentes / jabones', categoria: 'Contacto' },
  { id: 97,  nombre: 'Formaldehído', categoria: 'Contacto' },
  { id: 98,  nombre: 'Adhesivos / pegamentos', categoria: 'Contacto' },
  { id: 99,  nombre: 'Cinta adhesiva médica', categoria: 'Contacto' },
  { id: 100, nombre: 'Protector solar (químico)', categoria: 'Contacto' },
  { id: 101, nombre: 'Tinta de tatuaje', categoria: 'Contacto' },
  { id: 102, nombre: 'Resinas epóxicas', categoria: 'Contacto' },
  { id: 103, nombre: 'Ortiga / plantas urticantes', categoria: 'Contacto' },
  { id: 104, nombre: 'Hiedra venenosa (poison ivy)', categoria: 'Contacto' },
  { id: 105, nombre: 'Ejercicio (anafilaxia inducida por ejercicio)', categoria: 'Otro' },
  { id: 106, nombre: 'Alcohol / etanol', categoria: 'Otro' },
  { id: 107, nombre: 'Cafeína', categoria: 'Otro' },
  { id: 108, nombre: 'Transfusión sanguínea', categoria: 'Otro' },
  { id: 109, nombre: 'Silicona (implantes)', categoria: 'Otro' },
  { id: 110, nombre: 'Metales en implantes ortopédicos', categoria: 'Otro' },
  { id: 111, nombre: 'Idiopática (causa desconocida)', categoria: 'Otro' },
];

const CATEGORIAS_ALERGIAS = ['Todos', 'Medicamento', 'Alimento', 'Ambiental', 'Insecto/Veneno', 'Contacto', 'Otro'];

interface SiNoItem { id: string; label: string }

const PATOLOGICOS_LISTA: SiNoItem[] = [
  { id: 'diabetes',          label: 'Diabetes mellitus' },
  { id: 'hipertension',      label: 'Hipertensión arterial' },
  { id: 'cardiopatia',       label: 'Cardiopatía' },
  { id: 'epoc_asma',         label: 'EPOC / Asma' },
  { id: 'cancer',            label: 'Cáncer / Neoplasia maligna' },
  { id: 'epilepsia',         label: 'Epilepsia / Convulsiones' },
  { id: 'vih',               label: 'VIH / SIDA' },
  { id: 'hepatitis',         label: 'Hepatitis (A, B, C, D, E)' },
  { id: 'tuberculosis',      label: 'Tuberculosis' },
  { id: 'artritis',          label: 'Artritis reumatoide' },
  { id: 'lupus',             label: 'Lupus eritematoso sistémico' },
  { id: 'tiroides',          label: 'Enfermedad tiroidea' },
  { id: 'insuf_renal',       label: 'Insuficiencia renal crónica' },
  { id: 'insuf_hepatica',    label: 'Insuficiencia hepática' },
  { id: 'obesidad',          label: 'Obesidad (IMC > 35)' },
  { id: 'cirugias',          label: 'Cirugías previas' },
  { id: 'transfusiones',     label: 'Transfusiones sanguíneas' },
  { id: 'traumatismos',      label: 'Traumatismos graves' },
  { id: 'hospitalizaciones', label: 'Hospitalizaciones previas' },
  { id: 'otras_patologias',  label: 'Otra patología relevante' },
];

const NO_PATOLOGICOS_LISTA: SiNoItem[] = [
  { id: 'tabaquismo',          label: 'Tabaquismo' },
  { id: 'alcoholismo',         label: 'Alcoholismo' },
  { id: 'drogas',              label: 'Uso de drogas / sustancias' },
  { id: 'sedentarismo',        label: 'Sedentarismo' },
  { id: 'dieta_inadecuada',    label: 'Dieta inadecuada' },
  { id: 'automedicacion',      label: 'Automedicación frecuente' },
  { id: 'exposicion_quimicos', label: 'Exposición a químicos / tóxicos' },
  { id: 'trabajo_riesgo',      label: 'Trabajo de alto riesgo' },
  { id: 'insomnio',            label: 'Insomnio / Trastornos del sueño' },
];

const HEREDOFAM_LISTA: SiNoItem[] = [
  { id: 'diabetes',      label: 'Diabetes mellitus' },
  { id: 'hipertension',  label: 'Hipertensión arterial' },
  { id: 'cardiopatia',   label: 'Cardiopatía / Infarto' },
  { id: 'cancer',        label: 'Cáncer' },
  { id: 'epilepsia',     label: 'Epilepsia' },
  { id: 'enf_mental',    label: 'Enfermedades mentales' },
  { id: 'artritis',      label: 'Artritis reumatoide' },
  { id: 'tiroides',      label: 'Enfermedades tiroideas' },
  { id: 'malformaciones',label: 'Malformaciones congénitas' },
  { id: 'muerte_subita', label: 'Muerte súbita en jóvenes' },
  { id: 'trombosis',     label: 'Trombosis / Coagulopatías' },
  { id: 'nefropatia',    label: 'Nefropatía hereditaria' },
];

type TipoCampo = 'sinono' | 'texto' | 'numero' | 'fecha' | 'opciones';

interface Campo {
  id: string;
  label: string;
  tipo: TipoCampo;
  opciones?: string[];
  placeholder?: string;
  unidad?: string;
}

const GINECO_CAMPOS: Campo[] = [
  { id: 'menarquia',            label: 'Edad de menarquía',                tipo: 'numero', unidad: 'años' },
  { id: 'ritmo_menstrual',      label: 'Ritmo menstrual',                  tipo: 'texto',  placeholder: 'ej: 28x5' },
  { id: 'fum',                  label: 'Fecha última menstruación (FUM)',   tipo: 'fecha' },
  { id: 'gestas',               label: 'Número de gestas',                 tipo: 'numero' },
  { id: 'partos',               label: 'Partos vaginales',                 tipo: 'numero' },
  { id: 'cesareas',             label: 'Cesáreas',                         tipo: 'numero' },
  { id: 'abortos',              label: 'Abortos',                          tipo: 'numero' },
  { id: 'hijos_vivos',          label: 'Hijos vivos',                      tipo: 'numero' },
  { id: 'ultimo_pap',           label: 'Fecha último Papanicolaou',        tipo: 'fecha' },
  { id: 'resultado_pap',        label: 'Resultado Papanicolaou',           tipo: 'texto' },
  { id: 'metodo_anticonceptivo',label: 'Método anticonceptivo actual',     tipo: 'texto' },
  { id: 'menopausia',           label: '¿Menopausia?',                     tipo: 'sinono' },
  { id: 'lactancia',            label: '¿Lactancia activa?',               tipo: 'sinono' },
];

const PERINATALES_CAMPOS: Campo[] = [
  { id: 'tipo_parto',          label: 'Tipo de parto',                      tipo: 'opciones', opciones: ['Vaginal', 'Cesárea', 'Fórceps', 'Ventosa'] },
  { id: 'semanas_gestacion',   label: 'Semanas de gestación al nacer',      tipo: 'numero' },
  { id: 'peso_nacer',          label: 'Peso al nacer',                      tipo: 'texto',    placeholder: '3.2', unidad: 'kg' },
  { id: 'talla_nacer',         label: 'Talla al nacer',                     tipo: 'numero',   unidad: 'cm' },
  { id: 'apgar',               label: 'Apgar (1 y 5 min)',                  tipo: 'texto',    placeholder: 'ej: 8/9' },
  { id: 'hipoxia',             label: '¿Hipoxia neonatal?',                 tipo: 'sinono' },
  { id: 'ictericia',           label: '¿Ictericia neonatal?',               tipo: 'sinono' },
  { id: 'malformaciones',      label: '¿Malformaciones al nacer?',          tipo: 'sinono' },
  { id: 'ucin',                label: '¿Internamiento en UCIN?',            tipo: 'sinono' },
  { id: 'complicaciones',      label: 'Complicaciones perinatales',         tipo: 'texto' },
];

const POSTNATALES_CAMPOS: Campo[] = [
  { id: 'lactancia_materna',    label: 'Lactancia materna',                tipo: 'opciones', opciones: ['Exclusiva', 'Mixta', 'Ninguna'] },
  { id: 'meses_lactancia',      label: 'Duración de lactancia',            tipo: 'numero',   unidad: 'meses' },
  { id: 'sostuvo_cabeza',       label: 'Sostuvo cabeza sin apoyo',         tipo: 'numero',   unidad: 'meses' },
  { id: 'se_sento',             label: 'Se sentó sin apoyo',               tipo: 'numero',   unidad: 'meses' },
  { id: 'camino',               label: 'Caminó solo',                      tipo: 'numero',   unidad: 'meses' },
  { id: 'primeras_palabras',    label: 'Primeras palabras',                tipo: 'numero',   unidad: 'meses' },
  { id: 'control_esfinteres',   label: 'Control de esfínteres',            tipo: 'numero',   unidad: 'meses' },
  { id: 'desarrollo_normal',    label: '¿Desarrollo psicomotor normal?',   tipo: 'sinono' },
  { id: 'enf_infancia',         label: 'Enfermedades destacadas en infancia', tipo: 'texto' },
  { id: 'vacunacion_completa',  label: '¿Esquema de vacunación completo en infancia?', tipo: 'sinono' },
];

const PSIQUIATRICOS_CAMPOS: Campo[] = [
  { id: 'depresion',           label: '¿Depresión?',                           tipo: 'sinono' },
  { id: 'ansiedad',            label: '¿Trastorno de ansiedad?',               tipo: 'sinono' },
  { id: 'bipolar',             label: '¿Trastorno bipolar?',                   tipo: 'sinono' },
  { id: 'esquizofrenia',       label: '¿Esquizofrenia / Psicosis?',            tipo: 'sinono' },
  { id: 'tdah',                label: '¿TDA / TDAH?',                          tipo: 'sinono' },
  { id: 'trastorno_aliment',   label: '¿Trastorno alimenticio (AN, BN)?',      tipo: 'sinono' },
  { id: 'intentos_suicidio',   label: '¿Intentos de suicidio?',               tipo: 'sinono' },
  { id: 'tratamiento_actual',  label: '¿Tratamiento psiquiátrico actual?',     tipo: 'sinono' },
  { id: 'medicacion_psiq',     label: 'Medicación psiquiátrica actual',        tipo: 'texto' },
  { id: 'internamiento',       label: '¿Internamiento psiquiátrico previo?',   tipo: 'sinono' },
  { id: 'violencia',           label: '¿Antecedente de violencia intrafamiliar?', tipo: 'sinono' },
  { id: 'notas_psiq',          label: 'Observaciones adicionales',             tipo: 'texto' },
];

const DIETA_CAMPOS: Campo[] = [
  { id: 'tipo_dieta',          label: 'Tipo de dieta',                         tipo: 'opciones', opciones: ['Omnívora', 'Vegetariana', 'Vegana', 'Cetogénica', 'Sin gluten', 'Sin lactosa', 'Baja en sodio', 'Otra'] },
  { id: 'comidas_dia',         label: 'Comidas al día',                        tipo: 'opciones', opciones: ['1', '2', '3', '4', '5 o más'] },
  { id: 'agua_litros',         label: 'Agua al día',                           tipo: 'texto',    placeholder: '2.0', unidad: 'litros' },
  { id: 'frutas_verduras',     label: '¿Consume frutas y verduras diariamente?', tipo: 'sinono' },
  { id: 'lacteos',             label: '¿Consume lácteos?',                     tipo: 'sinono' },
  { id: 'proteina_animal',     label: '¿Consume proteína animal?',             tipo: 'sinono' },
  { id: 'ultraprocesados',     label: '¿Consume ultraprocesados frecuentemente?', tipo: 'sinono' },
  { id: 'suplementos',         label: 'Suplementos nutricionales',             tipo: 'texto' },
  { id: 'alimentos_excluidos', label: 'Alimentos excluidos',                   tipo: 'texto' },
  { id: 'problemas_digestivos',label: '¿Problemas digestivos frecuentes?',     tipo: 'sinono' },
  { id: 'desc_digestivos',     label: 'Descripción de problemas digestivos',   tipo: 'texto' },
  { id: 'objetivo_nutricional',label: 'Objetivo nutricional',                  tipo: 'opciones', opciones: ['Bajar de peso', 'Subir de peso', 'Mantener peso', 'Ganar músculo', 'Control de enfermedad', 'Otro'] },
];

// ─── Shared styles ─────────────────────────────────────────────────────────────

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white disabled:bg-gray-50 disabled:text-text-secondary';

// ─── Sub-components ────────────────────────────────────────────────────────────

function CheckRow({ checked, label, desc, onClick, readOnly }: { checked: boolean; label: string; desc?: string; onClick: () => void; readOnly: boolean }) {
  return (
    <div
      onClick={!readOnly ? onClick : undefined}
      className={cn(
        'flex items-start gap-3 p-2.5 rounded-lg border transition-colors select-none',
        !readOnly && 'cursor-pointer',
        checked ? 'bg-green-50 border-green-200' : 'border-gray-100',
        !readOnly && !checked && 'hover:bg-gray-50',
      )}
    >
      <div className={cn(
        'w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors',
        checked ? 'bg-green-500 border-green-500' : 'border-gray-300',
      )}>
        {checked && <Check size={9} className="text-white" strokeWidth={3} />}
      </div>
      <div>
        <p className="text-sm font-medium text-text-primary leading-snug">{label}</p>
        {desc && <p className="text-xs text-text-secondary mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

function SiNoToggle({ value, onChange, readOnly }: { value: boolean | undefined; onChange: (v: boolean) => void; readOnly: boolean }) {
  return (
    <div className="flex rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
      <button
        type="button"
        disabled={readOnly}
        onClick={() => onChange(true)}
        className={cn(
          'px-3 py-1 text-xs font-medium transition-colors',
          value === true ? 'bg-green-500 text-white' : 'text-text-secondary hover:bg-gray-50',
        )}
      >Sí</button>
      <button
        type="button"
        disabled={readOnly}
        onClick={() => onChange(false)}
        className={cn(
          'px-3 py-1 text-xs font-medium border-l border-gray-200 transition-colors',
          value === false ? 'bg-red-400 text-white' : 'text-text-secondary hover:bg-gray-50',
        )}
      >No</button>
    </div>
  );
}

function VacunasSection({ data, onUpdate, readOnly }: { data: Record<string, boolean>; onUpdate: (k: string, v: boolean) => void; readOnly: boolean }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs text-text-secondary mb-3">Marca las vacunas que el paciente tiene aplicadas según su esquema.</p>
      {VACUNAS_ESQUEMA.map(v => (
        <CheckRow
          key={v.id}
          checked={!!data[v.id]}
          label={v.nombre}
          desc={v.desc}
          onClick={() => onUpdate(v.id, !data[v.id])}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}

function AlergiasSection({ data, onUpdate, readOnly }: {
  data: { ids?: number[]; extras?: string };
  onUpdate: (val: { ids: number[]; extras?: string }) => void;
  readOnly: boolean;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState('Todos');
  const ids: number[] = data.ids || [];
  const selectedSet = new Set(ids);

  const filtradas = ALERGIAS_CATALOGO.filter(a =>
    (categoria === 'Todos' || a.categoria === categoria) &&
    (!busqueda || a.nombre.toLowerCase().includes(busqueda.toLowerCase()))
  );

  function toggleId(id: number) {
    if (readOnly) return;
    const newIds = selectedSet.has(id) ? ids.filter(i => i !== id) : [...ids, id];
    onUpdate({ ...data, ids: newIds });
  }

  return (
    <div className="space-y-4">
      {/* Selected chips */}
      {ids.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
            Seleccionadas ({ids.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {ids.map(id => {
              const a = ALERGIAS_CATALOGO.find(x => x.id === id);
              if (!a) return null;
              return (
                <span key={id} className="flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-full text-xs">
                  {a.nombre}
                  {!readOnly && (
                    <button type="button" onClick={() => toggleId(id)} className="hover:text-red-900">
                      <X size={10} />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
        <input
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar alergia..."
          className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        />
      </div>

      {/* Category pills */}
      <div className="flex flex-wrap gap-1.5">
        {CATEGORIAS_ALERGIAS.map(cat => (
          <button
            key={cat}
            type="button"
            onClick={() => setCategoria(cat)}
            className={cn(
              'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
              categoria === cat ? 'bg-primary-600 text-white' : 'border border-gray-200 text-text-secondary hover:border-gray-300',
            )}
          >{cat}</button>
        ))}
      </div>

      {/* Catalog list */}
      <div className="space-y-0.5 max-h-56 overflow-y-auto pr-1 border border-gray-100 rounded-lg p-2">
        {filtradas.length === 0 && (
          <p className="text-center py-4 text-xs text-text-secondary">Sin resultados para esta búsqueda</p>
        )}
        {filtradas.map(a => {
          const checked = selectedSet.has(a.id);
          return (
            <div
              key={a.id}
              onClick={() => toggleId(a.id)}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors',
                !readOnly && 'cursor-pointer',
                checked ? 'bg-red-50' : !readOnly && 'hover:bg-gray-50',
              )}
            >
              <div className={cn(
                'w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                checked ? 'bg-red-500 border-red-500' : 'border-gray-300',
              )}>
                {checked && <Check size={9} className="text-white" strokeWidth={3} />}
              </div>
              <span className="text-sm text-text-primary">{a.nombre}</span>
            </div>
          );
        })}
      </div>

      {/* Free-text extras */}
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1.5">
          Otras alergias no encontradas en el catálogo
        </label>
        <textarea
          value={data.extras || ''}
          onChange={e => !readOnly && onUpdate({ ids: data.ids ?? [], ...data, extras: e.target.value })}
          readOnly={readOnly}
          rows={2}
          placeholder="Describe alergias adicionales..."
          className={`${inputCls} resize-none`}
        />
      </div>
    </div>
  );
}

function SiNoSection({ items, data, onUpdate, readOnly }: {
  items: SiNoItem[];
  data: Record<string, { tiene?: boolean; notas?: string }>;
  onUpdate: (k: string, v: { tiene?: boolean; notas?: string }) => void;
  readOnly: boolean;
}) {
  return (
    <div className="divide-y divide-gray-50">
      {items.map(item => {
        const val = data[item.id] || {};
        return (
          <div key={item.id} className="py-2.5">
            <div className="flex items-center gap-3">
              <p className="flex-1 text-sm text-text-primary">{item.label}</p>
              <SiNoToggle
                value={val.tiene}
                onChange={v => onUpdate(item.id, { ...val, tiene: v })}
                readOnly={readOnly}
              />
            </div>
            {val.tiene === true && (
              <textarea
                value={val.notas || ''}
                onChange={e => !readOnly && onUpdate(item.id, { ...val, notas: e.target.value })}
                readOnly={readOnly}
                placeholder="Observaciones (medicamentos, fechas, detalles)..."
                rows={1}
                className="mt-1.5 w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-300 resize-none text-text-secondary"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function CamposSection({ campos, data, onUpdate, readOnly }: {
  campos: Campo[];
  data: Record<string, string>;
  onUpdate: (k: string, v: string) => void;
  readOnly: boolean;
}) {
  return (
    <div className="space-y-4">
      {campos.map(campo => {
        const value = data[campo.id] || '';

        if (campo.tipo === 'sinono') {
          const tiene  = value === 'true';
          const noTiene = value === 'false';
          return (
            <div key={campo.id} className="flex items-center gap-3 py-1 border-b border-gray-50 last:border-0">
              <label className="flex-1 text-sm text-text-primary">{campo.label}</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => onUpdate(campo.id, tiene ? '' : 'true')}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium transition-colors',
                    tiene ? 'bg-green-500 text-white' : 'text-text-secondary hover:bg-gray-50',
                  )}
                >Sí</button>
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => onUpdate(campo.id, noTiene ? '' : 'false')}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors',
                    noTiene ? 'bg-red-400 text-white' : 'text-text-secondary hover:bg-gray-50',
                  )}
                >No</button>
              </div>
            </div>
          );
        }

        return (
          <div key={campo.id}>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              {campo.label}
              {campo.unidad && <span className="ml-1 font-normal text-text-secondary/60">({campo.unidad})</span>}
            </label>
            {campo.tipo === 'opciones' ? (
              <select
                value={value}
                onChange={e => !readOnly && onUpdate(campo.id, e.target.value)}
                disabled={readOnly}
                className={inputCls}
              >
                <option value="">Seleccionar...</option>
                {campo.opciones!.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
            ) : campo.tipo === 'fecha' ? (
              <input
                type="date"
                value={value}
                onChange={e => !readOnly && onUpdate(campo.id, e.target.value)}
                readOnly={readOnly}
                className={inputCls}
              />
            ) : campo.tipo === 'numero' ? (
              <input
                type="number"
                value={value}
                min={0}
                onChange={e => !readOnly && onUpdate(campo.id, e.target.value)}
                readOnly={readOnly}
                placeholder={campo.placeholder}
                className={inputCls}
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={e => !readOnly && onUpdate(campo.id, e.target.value)}
                readOnly={readOnly}
                placeholder={campo.placeholder}
                className={inputCls}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

interface Props {
  id: string;
  hist: any;
  canEdit: boolean;
}

export function AntecedentesTab({ id, hist, canEdit }: Props) {
  const qc = useQueryClient();
  const [seccion, setSeccion] = useState<SeccionId>('vacunacion');
  const [allData, setAllData] = useState<Record<string, any>>({});
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);

  // Secciones habilitadas según el tipo de clínica (configuración > tipo de clínica)
  const { data: configClinica } = useQuery({
    queryKey: ['configuracion'],
    queryFn: () => api.get('/api/configuracion').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const seccionesVisibles = useMemo(() => {
    const habilitadas = (configClinica?.configuracionJson as any)?.antecedentesSecciones as Record<string, boolean> | undefined;
    return SECCIONES.filter(s => {
      if (SECCIONES_CONFIGURABLES.has(s.id) && habilitadas?.[s.id] === false) return false;
      return true;
    });
  }, [configClinica]);

  // Si la sección activa deja de estar disponible (p.ej. cambió el perfil de la clínica), cae a la primera visible
  useEffect(() => {
    if (!seccionesVisibles.some(s => s.id === seccion)) {
      setSeccion(seccionesVisibles[0]?.id ?? 'vacunacion');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seccionesVisibles]);

  function markDirty(v: boolean) {
    isDirtyRef.current = v;
    setIsDirty(v);
  }

  // Sync all sections from hist when it loads or updates (only if no unsaved edits)
  useEffect(() => {
    if (!isDirtyRef.current) {
      const newData: Record<string, any> = {};
      for (const colKey of Object.values(SECCION_KEYS)) {
        newData[colKey] = hist?.[colKey] ?? {};
      }
      setAllData(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hist]);

  const saveMutation = useMutation({
    mutationFn: (dto: any) => api.put(`/api/pacientes/${id}/antecedentes`, dto).then(r => r.data),
    onSuccess: (saved) => {
      qc.setQueryData(['expediente', id], (old: any) => ({ ...old, historiaClinica: saved }));
      toast.success('Antecedentes guardados');
      markDirty(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar los antecedentes')),
  });

  const currentKey = SECCION_KEYS[seccion];
  const currentData = allData[currentKey] ?? {};

  function updateField(key: string, val: any) {
    setAllData(prev => ({ ...prev, [currentKey]: { ...(prev[currentKey] ?? {}), [key]: val } }));
    markDirty(true);
  }

  function updateFull(val: any) {
    setAllData(prev => ({ ...prev, [currentKey]: val }));
    markDirty(true);
  }

  const activeSeccion = SECCIONES.find(s => s.id === seccion)!;

  return (
    <div className="flex flex-col md:flex-row gap-0">
      {/* ── Mobile: section select ── */}
      <div className="md:hidden relative mb-3">
        <select
          value={seccion}
          onChange={e => setSeccion(e.target.value as SeccionId)}
          className="w-full appearance-none px-4 py-2.5 pr-10 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          {seccionesVisibles.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </div>

      {/* ── Desktop: Left nav ── */}
      <div className="hidden md:block w-44 flex-shrink-0 border-r border-gray-100 pr-3 mr-4 self-start sticky top-0">
        {seccionesVisibles.map(s => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSeccion(s.id)}
            className={cn(
              'flex items-center gap-2 w-full text-left px-3 py-2.5 rounded-lg text-xs mb-0.5 transition-colors',
              seccion === s.id
                ? 'bg-primary-50 text-primary-700 font-semibold'
                : 'text-text-secondary hover:bg-gray-50 hover:text-text-primary',
            )}
          >
            <s.icon size={13} className="flex-shrink-0" />
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 min-w-0">
        {/* Section header */}
        <div className="flex items-center gap-2 mb-4">
          <activeSeccion.icon size={15} className="text-primary-600 flex-shrink-0" />
          <h3 className="font-semibold text-text-primary text-sm">{activeSeccion.label}</h3>
        </div>

        {/* Section content */}
        <div>
          {seccion === 'vacunacion' && (
            <VacunasSection
              data={currentData as Record<string, boolean>}
              onUpdate={(k, v) => updateField(k, v)}
              readOnly={!canEdit}
            />
          )}

          {seccion === 'alergias' && (
            <AlergiasSection
              data={currentData as { ids?: number[]; extras?: string }}
              onUpdate={updateFull}
              readOnly={!canEdit}
            />
          )}

          {(seccion === 'patologicos' || seccion === 'noPatologicos' || seccion === 'heredofamiliares') && (
            <SiNoSection
              items={
                seccion === 'patologicos' ? PATOLOGICOS_LISTA :
                seccion === 'noPatologicos' ? NO_PATOLOGICOS_LISTA :
                HEREDOFAM_LISTA
              }
              data={currentData as Record<string, { tiene?: boolean; notas?: string }>}
              onUpdate={(k, v) => updateField(k, v)}
              readOnly={!canEdit}
            />
          )}

          {(seccion === 'gineco' || seccion === 'perinatales' || seccion === 'postnatales' || seccion === 'psiquiatricos' || seccion === 'dieta') && (
            <CamposSection
              campos={
                seccion === 'gineco'        ? GINECO_CAMPOS :
                seccion === 'perinatales'   ? PERINATALES_CAMPOS :
                seccion === 'postnatales'   ? POSTNATALES_CAMPOS :
                seccion === 'psiquiatricos' ? PSIQUIATRICOS_CAMPOS :
                DIETA_CAMPOS
              }
              data={currentData as Record<string, string>}
              onUpdate={(k, v) => updateField(k, v)}
              readOnly={!canEdit}
            />
          )}
        </div>

        {/* Save button */}
        {canEdit && (
          <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
            {isDirty
              ? <span className="text-xs text-amber-600">Cambios sin guardar</span>
              : <span />
            }
            <button
              type="button"
              onClick={() => saveMutation.mutate(allData)}
              disabled={!isDirty || saveMutation.isPending}
              className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={14} />
              {saveMutation.isPending ? 'Guardando...' : 'Guardar antecedentes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
